import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { PageHeader, SearchBar } from 'components';
import AddIcon from '@material-ui/icons/Add';

import DeviceListTable from './DeviceListTable.jsx';
import _ from 'lodash';
import { useOrganizations, useSettings } from 'hooks';
import { useMediaQuery } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  toggleButtonGroup: {
    maxHeight: '35px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
  tableSpacing: {
    margin: theme.spacing(2),
  },
  spacing: {
    marginBottom: theme.spacing(2),
  },
  listTable: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
}));

const DeviceListView = ({
  assignedDevices,
  unassignedDevices,
  openModelHandler,
  clearSelected,
  setClearSelected,
  setShowDeviceDetails,
  setSelection,
}) => {
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const { t } = useTranslation();
  const classes = useStyles();
  const [selectedDevicesIds, setSelectedDevicesIds] = useState([]);
  const [searchText, setSearchText] = useState('');
  const { isSuper } = useSettings();
  const { selectedOrganization } = useOrganizations();
  const pageProps = {};

  const openModel = (modelAction) => {
    const devices = sortIDsToDevices();
    openModelHandler(modelAction, devices);
  };

  if (clearSelected && selectedDevicesIds.length > 0) {
    setSelectedDevicesIds([]);
  }

  pageProps.actionFab = true;
  pageProps.icon = <AddIcon />;

  if (selectedDevicesIds.length > 1) {
    pageProps.action = 'devices_edit';
    pageProps.actionHref = () => {
      openModel('devices');
    };
  } else if (selectedDevicesIds.length === 1) {
    pageProps.action = 'device_edit';
    pageProps.actionHref = () => {
      openModel('device');
    };
  } else if (selectedDevicesIds.length === 0 && isSuper) {
    pageProps.action = 'device_add';
    pageProps.actionHref = () => {
      openModel('device');
    };
  } else {
    pageProps.actionFab = false;
  }
  const onDeviceCheckboxClick = (selected, selectAll) => {
    if (clearSelected) {
      setClearSelected(false);
    }
    const convertToIDArray = [];
    if (!Array.isArray(selected)) {
      convertToIDArray.push(selected);
    } else {
      convertToIDArray.push(...selected);
    }
    const devicesIds = _.map(convertToIDArray, 'id');
    let intersetion = [];
    if (!selectAll) {
      intersetion = _.intersectionWith(selectedDevicesIds, devicesIds, _.isEqual);
    }
    if (intersetion.length > 0) {
      const result = _.differenceWith(selectedDevicesIds, devicesIds, _.isEqual);
      setSelectedDevicesIds(result);
    } else {
      const result = _.unionWith(selectedDevicesIds, devicesIds, _.isEqual);
      setSelectedDevicesIds(result);
    }
  };

  const sortIDsToDevices = () => {
    const result = [];
    _.forEach(selectedDevicesIds, (id) => {
      const device = _.find(assignedDevices, { id });
      if (device) {
        result.push(device);
      } else {
        result.push(_.find(unassignedDevices, { id }));
      }
    });
    return result;
  };

  const selectSingeDevice = (event, device) => {
    setSelection(device);
    setShowDeviceDetails(true);
  };

  return (
    <div>
      <PageHeader title="device_management" {...pageProps} className={classes.header} />
      <SearchBar className={classes.spacing} selector={searchText} setSearchText={setSearchText} />
      <Grid container direction="row">
        <Grid item sm={6} xs={12} className={classes.listTable} key={`primary_assigned`}>
          <DeviceListTable
            className={classes.listTable}
            devices={assignedDevices}
            handleDeviceSelect={(device) => {
              selectSingeDevice(device);
            }}
            title={assignedDevices && assignedDevices.length > 0 ? t(`assigned_pos`) : t('no_assigned_pos')}
            selectedDevicesIds={selectedDevicesIds}
            onDeviceCheckboxClick={onDeviceCheckboxClick}
            selectAll={onDeviceCheckboxClick}
            selectSingleDevice={selectSingeDevice}
            searchText={searchText}
          />
        </Grid>
        {mobileView && <div className={classes.tableSpacing} />}
        <Grid item sm={6} xs={12} key={`secondary_assigned`}>
          <DeviceListTable
            className={classes.listTable}
            devices={unassignedDevices}
            handleDeviceSelect={(device) => {
              selectSingeDevice(device);
            }}
            title={
              isSuper || selectedOrganization
                ? unassignedDevices && unassignedDevices.length > 0
                  ? t(`assigned_neg`)
                  : t('no_assigned_neg')
                : t('device_plural')
            }
            setSelectedDevicesIds={setSelectedDevicesIds}
            selectedDevicesIds={selectedDevicesIds}
            onDeviceCheckboxClick={onDeviceCheckboxClick}
            selectAll={onDeviceCheckboxClick}
            selectSingleDevice={selectSingeDevice}
            searchText={searchText}
          />
        </Grid>
      </Grid>
    </div>
  );
};

DeviceListView.propTypes = {
  assignedDevices: PropTypes.array,
  unassignedDevices: PropTypes.array,
  openModelHandler: PropTypes.func.isRequired,
  clearSelected: PropTypes.bool,
  setClearSelected: PropTypes.func,
  setShowDeviceDetails: PropTypes.func,
  setSelection: PropTypes.func,
};

DeviceListView.defaultProps = {
  assignedDevices: [],
  unassignedDevices: [],
};

export default DeviceListView;
